import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { OrgBillingCard } from '../components/OrgBillingCard';
import { OrgAIProvidersCard } from '../components/OrgAIProvidersCard';
import { AdminAPI, Organization, OrgModule, OrgLeadInfo, OrgCreditsOverview } from '../lib/api';
import {
  labelFor,
  ORG_STATUS_LABELS,
  USER_ROLE_LABELS,
  USER_STATUS_LABELS,
  MODULE_LABELS,
  USE_CASE_LABELS,
  HOW_HEARD_LABELS,
} from '../lib/labels';
import { Edit2, Trash2, Save, X, RefreshCw, Loader, UserPlus, Info, KeyRound, ToggleLeft, ToggleRight, Zap, Navigation, ClipboardList, Bot, Phone } from 'lucide-react';

const COMPANY_SIZE_LABELS: Record<string, string> = {
  '1-10': '1-10 employees',
  '11-50': '11-50 employees',
  '51-200': '51-200 employees',
  '201-500': '201-500 employees',
  '501+': '501+ employees',
};
const reasonOf = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** Trial / Active / Trial ended, from organizations.billing_state (28 Sep). */
function stageLabel(state: string | null | undefined, trialDaysLeft?: number | null): string {
  if (state === 'trial') return trialDaysLeft != null ? `Trial · ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} left` : 'Trial';
  if (state === 'trial_expired') return 'Trial ended';
  if (!state) return '—';
  return 'Active';
}

interface User {
  id: string;
  email: string;
  // API returns camelCase (Pydantic by_alias=True)
  firstName: string | null;
  lastName: string | null;
  role: string;
  status: string;
  createdAt: string;
  cognitoUsername?: string | null;
  lastLogin?: string | null;
}

export function OrganizationDetailsEnhanced() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersError, setUsersError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editMaxUsers, setEditMaxUsers] = useState(0);
  const [editStatus, setEditStatus] = useState('');

  // User edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserData, setEditUserData] = useState<{
    firstName: string;
    lastName: string;
    role: string;
    status: string;
  }>({ firstName: '', lastName: '', role: '', status: '' });

  // Sync state
  const [syncing, setSyncing] = useState(false);

  // Module management state
  const [modules, setModules] = useState<OrgModule[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState('');
  const [togglingModule, setTogglingModule] = useState<string | null>(null);

  // Signup / lead info captured at trial signup
  const [leadInfo, setLeadInfo] = useState<OrgLeadInfo | null>(null);
  const [leadInfoError, setLeadInfoError] = useState('');

  // 28 Sep (mig 0702): the one balance + trial stage, from the credits read
  // model. The conversation and navigation counters are retired.
  const [credits, setCredits] = useState<OrgCreditsOverview | null>(null);

  // Add user modal state
  const [showAddUser, setShowAddUser] = useState(false);

  // Reset password modal state
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserFirstName, setAddUserFirstName] = useState('');
  const [addUserLastName, setAddUserLastName] = useState('');
  const [addUserRole, setAddUserRole] = useState('user');
  const [addUserPassword, setAddUserPassword] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => {
    if (token) {
      loadOrganization();
      loadUsers();
      loadModules();
      loadLeadInfo();
      loadCredits();
    }
  }, [id, token]);

  const loadCredits = async () => {
    try {
      setCredits(await new AdminAPI(token!).getOrgCreditsOverview(id!));
    } catch {
      setCredits(null);
    }
  };

  const loadLeadInfo = async () => {
    try {
      setLeadInfoError('');
      const api = new AdminAPI(token!);
      setLeadInfo(await api.getOrgLeadInfo(id!));
    } catch (err) {
      setLeadInfo(null);
      setLeadInfoError(reasonOf(err));
    }
  };

  const loadOrganization = async () => {
    try {
      const api = new AdminAPI(token!);
      const orgs = await api.getOrganizations(0, 1000);
      const org = orgs.find(o => o.id === id);
      if (org) {
        setOrganization(org);
        setEditMaxUsers(org.max_users);
        setEditStatus(org.status);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      setModulesLoading(true);
      setModulesError('');
      const api = new AdminAPI(token!);
      const result = await api.getOrgModules(id!);
      setModules(result);
    } catch (err) {
      setModules([]);
      setModulesError(reasonOf(err));
    } finally {
      setModulesLoading(false);
    }
  };

  const handleToggleModule = async (moduleName: string, currentEnabled: boolean) => {
    if (modulesError) return;
    try {
      setTogglingModule(moduleName);
      setError('');
      const api = new AdminAPI(token!);
      const updated = await api.setOrgModule(id!, moduleName, !currentEnabled);
      // A default-deny module (e.g. phone_agents) has no row until first granted,
      // so add it if the org didn't already have it; otherwise update in place.
      setModules(prev => prev.some(m => m.module_name === moduleName)
        ? prev.map(m => m.module_name === moduleName ? updated : m)
        : [...prev, updated]);
      setSuccess(`${labelFor(MODULE_LABELS, moduleName)} module ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update module');
    } finally {
      setTogglingModule(null);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersError('');
      const api = new AdminAPI(token!);
      const orgUsers = await api.getOrganizationUsers(id!);
      setUsers(orgUsers);
    } catch (err) {
      setUsers([]);
      setUsersError(reasonOf(err));
    }
  };

  const handleSave = async () => {
    if (!organization) return;
    
    try {
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      
      await api.updateOrganization(id!, {
        max_users: editMaxUsers,
        status: editStatus
      });
      
      setSuccess('Organization updated');
      setIsEditing(false);
      await loadOrganization();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organization');
    }
  };

  const handleCancel = () => {
    if (organization) {
      setEditMaxUsers(organization.max_users);
      setEditStatus(organization.status);
    }
    setIsEditing(false);
  };

  const startEditUser = (user: User) => {
    setEditingUserId(user.id);
    setEditUserData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      status: user.status
    });
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditUserData({ firstName: '', lastName: '', role: '', status: '' });
  };

  const saveUser = async (userId: string) => {
    try {
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      
      await api.updateUser(userId, {
        firstName: editUserData.firstName,
        lastName: editUserData.lastName,
        role: editUserData.role,
        status: editUserData.status,
      });
      
      setSuccess('User updated');
      setEditingUserId(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const deleteUser = async (userId: string, userEmail: string, userRole: string) => {
    const isOwner = userRole === 'org_owner';
    const msg = isOwner
      ? `${userEmail} is the owner of this organization. Deleting them removes their sign-in (AWS Cognito) and all their sessions. This cannot be undone. Continue?`
      : `Delete ${userEmail}? This removes their sign-in and all their sessions. This cannot be undone.`;
    if (!window.confirm(msg)) return;

    try {
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      await api.deleteUser(userId);
      setSuccess(`${userEmail} deleted`);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleSyncFromAWS = async () => {
    if (!confirm('This will synchronize all users and organizations from AWS Cognito. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      
      const result = await api.syncFromAWS();
      
      setSuccess(result.message);
      await loadUsers();
      await loadOrganization();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to synchronize from AWS');
    } finally {
      setSyncing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !resetPassword) {
      setError('Password is required');
      return;
    }
    try {
      setResettingPassword(true);
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      await api.setUserPassword(resetPasswordUser.id, resetPassword);
      setSuccess(`Password reset for ${resetPasswordUser.email}. A notification email has been sent.`);
      setResetPasswordUser(null);
      setResetPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleAddUser = async () => {
    if (!addUserEmail || !addUserPassword) {
      setError('Email and password are required');
      return;
    }
    try {
      setAddingUser(true);
      setError('');
      setSuccess('');
      const api = new AdminAPI(token!);
      await api.createUser(id!, {
        email: addUserEmail,
        firstName: addUserFirstName || undefined,
        lastName: addUserLastName || undefined,
        role: addUserRole,
        password: addUserPassword,
      });
      setSuccess(`${addUserEmail} created`);
      setShowAddUser(false);
      setAddUserEmail('');
      setAddUserFirstName('');
      setAddUserLastName('');
      setAddUserRole('user');
      setAddUserPassword('');
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading organization...</div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600">Organization not found. It may have been deleted.</div>
          {error && <div className="text-sm text-gray-500 mt-1">{error}</div>}
          <Link to="/organizations" className="inline-block mt-4 text-blue-600 hover:text-blue-700">
            Back to organizations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/organizations')}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
            >
              ← Back to organizations
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{organization.name}</h1>
            <p className="text-gray-500 mt-1">{organization.slug}</p>
          </div>

          {/* Sync Button */}
          <button
            onClick={handleSyncFromAWS}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {syncing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Synchronizing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Sync from AWS
              </>
            )}
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Credits & terms (28 Sep, mig 0702): the one balance, the agreed
            per-lead and success fees, the org's rate card, grants, the ledger. */}
        {id && <div id="credits" className="mb-8 scroll-mt-6"><OrgBillingCard orgId={id} onChanged={loadCredits} /></div>}
        {id && <OrgAIProvidersCard orgId={id} />}

        {/* Organization Details Card */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Organization details</h2>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Edit limits &amp; status
              </button>
            ) : (
              <div className="space-x-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save changes
                </button>
              </div>
            )}
          </div>
          
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Organization ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization ID
                </label>
                <div className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">
                  {organization.id}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                {isEditing ? (
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                ) : (
                  <div className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                    organization.status === 'active' ? 'bg-green-100 text-green-800' :
                    organization.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {labelFor(ORG_STATUS_LABELS, organization.status)}
                  </div>
                )}
              </div>

              {/* Credits left — the org's one balance (28 Sep, mig 0702). The
                  conversation-credits editor is gone: credits are added as
                  grants on the Credits & terms card above. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credits left
                </label>
                <div className="text-gray-900 text-lg font-semibold tabular-nums">
                  {credits ? (credits.credits_left ?? credits.balance).toLocaleString() : '—'}
                  <a href="#credits" className="ml-2 text-sm font-normal text-blue-600 hover:text-blue-700">Credits &amp; terms</a>
                </div>
              </div>

              {/* Max Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max users
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    value={editMaxUsers}
                    onChange={(e) => setEditMaxUsers(parseInt(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="1"
                  />
                ) : (
                  <div className="text-gray-900 text-lg font-semibold">
                    {(organization.max_users ?? 0).toLocaleString()}
                    <span className="text-sm text-gray-500 ml-2">
                      ({(organization.current_users ?? 0).toLocaleString()} current)
                    </span>
                  </div>
                )}
              </div>

              {/* Stage — trial or not, from billing_state (plans are retired, 28 Sep). */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stage
                </label>
                <div className="text-gray-900">
                  {stageLabel(credits?.billing_state ?? organization.billing_state, credits?.trial_days_left)}
                </div>
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                </label>
                <div className="text-gray-900">
                  {organization.domain || 'Not set'}
                </div>
              </div>

              {/* Created At */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created
                </label>
                <div className="text-gray-900">
                  {new Date(organization.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signup details (captured at trial signup) */}
        {leadInfoError && (
          <div className="bg-white rounded-lg shadow mb-8 px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-gray-400" />
              <div>
                <div className="font-medium text-gray-900">Signup details unavailable</div>
                <div className="text-sm text-gray-500">{leadInfoError}</div>
              </div>
            </div>
            <button
              onClick={loadLeadInfo}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 shrink-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}
        {leadInfo && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-gray-500" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Signup details</h2>
                <p className="text-sm text-gray-500">Information provided when this organization started its trial</p>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary use case</label>
                  <div className="text-gray-900">
                    {leadInfo.primary_use_case
                      ? labelFor(USE_CASE_LABELS, leadInfo.primary_use_case)
                      : <span className="text-gray-400 italic">Not specified</span>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company size</label>
                  <div className="text-gray-900">
                    {leadInfo.company_size
                      ? (COMPANY_SIZE_LABELS[leadInfo.company_size] || leadInfo.company_size)
                      : <span className="text-gray-400 italic">Not specified</span>}
                  </div>
                </div>

                {leadInfo.use_case_description && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Use case details
                      {leadInfo.primary_use_case === 'other' && (
                        <span className="ml-2 text-xs font-normal text-amber-600">(provided for "Other")</span>
                      )}
                    </label>
                    <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded p-3 text-sm">
                      {leadInfo.use_case_description}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How they heard about us</label>
                  <div className="text-gray-900">
                    {leadInfo.how_did_you_hear
                      ? labelFor(HOW_HEARD_LABELS, leadInfo.how_did_you_hear)
                      : <span className="text-gray-400 italic">Not specified</span>}
                  </div>
                </div>
                {leadInfo.referral_source && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Referral source</label>
                    <div className="text-gray-900">{leadInfo.referral_source}</div>
                  </div>
                )}
                {leadInfo.current_solution && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current solution</label>
                    <div className="text-gray-900">{leadInfo.current_solution}</div>
                  </div>
                )}
                {leadInfo.estimated_monthly_conversations && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated monthly conversations</label>
                    <div className="text-gray-900">{leadInfo.estimated_monthly_conversations}</div>
                  </div>
                )}
                {leadInfo.estimated_monthly_visitors && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated monthly visitors</label>
                    <div className="text-gray-900">{leadInfo.estimated_monthly_visitors}</div>
                  </div>
                )}
                {leadInfo.site_description && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site description</label>
                    <div className="text-gray-900 whitespace-pre-wrap">{leadInfo.site_description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Module access & navigation credits */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Module access</h2>
            <p className="text-sm text-gray-500 mt-1">Enable or disable platform modules for this organization</p>
          </div>

          {modulesError && !modulesLoading && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start justify-between gap-4">
              <div>
                <div>Couldn&apos;t load this organization&apos;s modules. Retry before changing anything &mdash; the toggles below may not reflect reality.</div>
                <div className="text-xs text-red-600 mt-1">{modulesError}</div>
              </div>
              <button
                onClick={loadModules}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          )}

          {modulesLoading ? (
            <div className="px-6 py-8 flex items-center justify-center">
              <Loader className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-500">Loading modules...</span>
            </div>
          ) : (
            <div className="px-6 py-4 space-y-4">
              {/* Agents Module */}
              {(() => {
                const agentsMod = modules.find(m => m.module_name === 'agents');
                const isEnabled = agentsMod?.is_enabled ?? false;
                const isToggling = togglingModule === 'agents';
                return (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Zap className={`w-5 h-5 ${isEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{MODULE_LABELS.agents}</div>
                        <div className="text-sm text-gray-500">AI agent conversations and API access</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${!modulesError && isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                        {modulesError ? 'Unknown' : isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleModule('agents', isEnabled)}
                        disabled={isToggling || modulesError !== ''}
                        className="focus:outline-none disabled:opacity-50"
                        title={isEnabled ? 'Disable agents module' : 'Enable agents module'}
                      >
                        {isToggling ? (
                          <Loader className="w-8 h-8 animate-spin text-gray-400" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-blue-600 cursor-pointer hover:text-blue-700" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Agentic Navigation Module */}
              {(() => {
                const navMod = modules.find(m => m.module_name === 'agentic_navigation');
                const isEnabled = navMod?.is_enabled ?? false;
                const isToggling = togglingModule === 'agentic_navigation';
                return (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-purple-100' : 'bg-gray-100'}`}>
                        <Navigation className={`w-5 h-5 ${isEnabled ? 'text-purple-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{MODULE_LABELS.agentic_navigation}</div>
                        <div className="text-sm text-gray-500">Embeddable navigation copilot widget</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${!modulesError && isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                        {modulesError ? 'Unknown' : isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleModule('agentic_navigation', isEnabled)}
                        disabled={isToggling || modulesError !== ''}
                        className="focus:outline-none disabled:opacity-50"
                        title={isEnabled ? 'Disable agentic navigation' : 'Enable agentic navigation'}
                      >
                        {isToggling ? (
                          <Loader className="w-8 h-8 animate-spin text-gray-400" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-purple-600 cursor-pointer hover:text-purple-700" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Avatar Module */}
              {(() => {
                const avatarMod = modules.find(m => m.module_name === 'avatar');
                const isEnabled = avatarMod?.is_enabled ?? false;
                const isToggling = togglingModule === 'avatar';
                return (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-rose-100' : 'bg-gray-100'}`}>
                        <Bot className={`w-5 h-5 ${isEnabled ? 'text-rose-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{MODULE_LABELS.avatar}</div>
                        <div className="text-sm text-gray-500">Animated character launcher for the navigation widget</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${!modulesError && isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                        {modulesError ? 'Unknown' : isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleModule('avatar', isEnabled)}
                        disabled={isToggling || modulesError !== ''}
                        className="focus:outline-none disabled:opacity-50"
                        title={isEnabled ? 'Disable avatar' : 'Enable avatar'}
                      >
                        {isToggling ? (
                          <Loader className="w-8 h-8 animate-spin text-gray-400" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-rose-600 cursor-pointer hover:text-rose-700" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Phone Agents Module (FO-54) — granted like Agents / Agentic Navigation */}
              {(() => {
                const phoneMod = modules.find(m => m.module_name === 'phone_agents');
                const isEnabled = phoneMod?.is_enabled ?? false;
                const isToggling = togglingModule === 'phone_agents';
                return (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-teal-100' : 'bg-gray-100'}`}>
                        <Phone className={`w-5 h-5 ${isEnabled ? 'text-teal-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{MODULE_LABELS.phone_agents}</div>
                        <div className="text-sm text-gray-500">Voice-first telephony agents (inbound/outbound calls, SMS handoff)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${!modulesError && isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                        {modulesError ? 'Unknown' : isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleModule('phone_agents', isEnabled)}
                        disabled={isToggling || modulesError !== ''}
                        className="focus:outline-none disabled:opacity-50"
                        title={isEnabled ? 'Disable phone agents' : 'Enable phone agents'}
                      >
                        {isToggling ? (
                          <Loader className="w-8 h-8 animate-spin text-gray-400" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-teal-600 cursor-pointer hover:text-teal-700" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Campaigns Module (Exotel omnichannel) — outbound campaign dialing */}
              {(() => {
                const campaignsMod = modules.find(m => m.module_name === 'campaigns');
                const isEnabled = campaignsMod?.is_enabled ?? false;
                const isToggling = togglingModule === 'campaigns';
                return (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-amber-100' : 'bg-gray-100'}`}>
                        <Phone className={`w-5 h-5 ${isEnabled ? 'text-amber-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{MODULE_LABELS.campaigns}</div>
                        <div className="text-sm text-gray-500">Outbound call campaigns (Exotel dialer, consent-gated, DND-compliant)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${!modulesError && isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                        {modulesError ? 'Unknown' : isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleModule('campaigns', isEnabled)}
                        disabled={isToggling || modulesError !== ''}
                        className="focus:outline-none disabled:opacity-50"
                        title={isEnabled ? 'Disable campaigns' : 'Enable campaigns'}
                      >
                        {isToggling ? (
                          <Loader className="w-8 h-8 animate-spin text-gray-400" />
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-amber-600 cursor-pointer hover:text-amber-700" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}

            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Users{usersError ? '' : ` (${users.length.toLocaleString()})`}</h2>
            <button
              onClick={() => setShowAddUser(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add user
            </button>
          </div>

          {usersError ? (
            <div className="mx-6 my-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start justify-between gap-4">
              <div>
                <div>Couldn&apos;t load this organization&apos;s users. Retry, or reload the page.</div>
                <div className="text-xs text-red-600 mt-1">{usersError}</div>
              </div>
              <button
                onClick={loadUsers}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No users in this organization yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUserId === user.id ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editUserData.firstName}
                              onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
                              placeholder="First name"
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              type="text"
                              value={editUserData.lastName}
                              onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
                              placeholder="Last name"
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        ) : (
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : <span className="text-gray-400 italic">No name</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <div className="text-sm text-gray-900">{user.email}</div>
                          {user.cognitoUsername && (
                            <div className="relative group">
                              <button
                                type="button"
                                aria-label="Show sign-in ID"
                                className="flex items-center text-gray-400 hover:text-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
                              >
                                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                              </button>
                              <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block group-focus-within:block z-10 w-72 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                                <div className="font-semibold mb-1 text-gray-300">Sign-in ID (AWS Cognito)</div>
                                <div className="font-mono break-all">{user.cognitoUsername}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUserId === user.id ? (
                          <select
                            value={editUserData.role}
                            onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.role === 'org_owner' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'org_admin' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {labelFor(USER_ROLE_LABELS, user.role)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUserId === user.id ? (
                          <select
                            value={editUserData.status}
                            onChange={(e) => setEditUserData({ ...editUserData, status: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="active">Active</option>
                            <option value="invited">Invited</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.status === 'active' ? 'bg-green-100 text-green-800' :
                            user.status === 'invited' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {labelFor(USER_STATUS_LABELS, user.status)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingUserId === user.id ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => saveUser(user.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Save"
                            >
                              <Save className="w-5 h-5" />
                            </button>
                            <button
                              onClick={cancelEditUser}
                              className="text-gray-600 hover:text-gray-900"
                              title="Cancel"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => startEditUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setResetPasswordUser(user); setResetPassword(''); setError(''); }}
                              className="text-amber-600 hover:text-amber-900"
                              title="Reset password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteUser(user.id, user.email, user.role)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add user</h3>
              <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={addUserEmail}
                  onChange={(e) => setAddUserEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                  <input
                    type="text"
                    value={addUserFirstName}
                    onChange={(e) => setAddUserFirstName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="First"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                  <input
                    type="text"
                    value={addUserLastName}
                    onChange={(e) => setAddUserLastName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="Last"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={addUserRole}
                  onChange={(e) => setAddUserRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={addUserPassword}
                  onChange={(e) => setAddUserPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddUser(false)}
                disabled={addingUser}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                disabled={addingUser || !addUserEmail || !addUserPassword}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {addingUser ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {addingUser ? 'Creating...' : 'Create user'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Reset password</h3>
              <button onClick={() => setResetPasswordUser(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Set a new password for <strong>{resetPasswordUser.email}</strong>.
              A notification email will be sent with the new credentials.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Minimum 8 characters"
              />
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setResetPasswordUser(null)}
                disabled={resettingPassword}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resettingPassword || !resetPassword}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resettingPassword ? <Loader className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {resettingPassword ? 'Resetting...' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}







